import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Phone, 
  AlertTriangle, 
  Copy, 
  X,
  CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmergencyCallButton = ({ 
  jobId = null, 
  variant = "full", // "full" | "compact" | "prominent"
  className = "" 
}) => {
  const { user } = useAuth();
  const [hotlineNumber, setHotlineNumber] = useState('1800 000 000');
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHotlineNumber();
  }, []);

  const fetchHotlineNumber = async () => {
    try {
      const response = await axios.get(`${API}/config/support_hotline_number`);
      setHotlineNumber(response.data.value || '1800 000 000');
    } catch (error) {
      console.error('Failed to fetch hotline number:', error);
      // Use default if config fetch fails
      setHotlineNumber('1800 000 000');
    }
  };

  const detectPlatform = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod|android/.test(userAgent)) {
      return 'mobile';
    }
    return 'desktop';
  };

  const handleEmergencyCall = async () => {
    setLoading(true);

    try {
      // Log the emergency call attempt
      const response = await axios.post(`${API}/emergency/call`, {
        job_id: jobId
      });

      const platform = detectPlatform();
      
      if (platform === 'mobile') {
        // Mobile: Open native dialer
        const telLink = `tel:${hotlineNumber.replace(/\s/g, '')}`;
        window.location.href = telLink;
        toast.success('Opening dialer...');
      } else {
        // Desktop: Show modal with number
        setShowModal(true);
      }

    } catch (error) {
      console.error('Failed to log emergency call:', error);
      toast.error('Failed to access emergency hotline');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(hotlineNumber);
      setCopied(true);
      toast.success('Hotline number copied to clipboard');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy number');
    }
  };

  const formatHotlineForDisplay = (number) => {
    // Format for better readability: 1800 000 000 or 13 11 66
    return number.replace(/(\d{2,4})(\d{3})(\d{3})/g, '$1 $2 $3').trim();
  };

  if (variant === "compact") {
    return (
      <>
        <button
          onClick={handleEmergencyCall}
          disabled={loading}
          className={`flex items-center justify-center p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors ${className}`}
          title="Emergency Breakdown Hotline"
        >
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
          ) : (
            <Phone className="h-5 w-5" />
          )}
        </button>

        {/* Desktop Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                  Emergency Hotline
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <p className="text-gray-600 mb-2">JOP Emergency Breakdown Hotline:</p>
                {jobId && (
                  <p className="text-sm text-gray-500 mb-4">
                    Related to Job: {jobId.slice(0, 8).toUpperCase()}
                  </p>
                )}
                <div className="text-3xl font-bold text-red-600 mb-6">
                  {formatHotlineForDisplay(hotlineNumber)}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={copyToClipboard}
                    className="jop-button-secondary flex-1 flex items-center justify-center"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Number
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => window.location.href = `tel:${hotlineNumber.replace(/\s/g, '')}`}
                    className="jop-button flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  if (variant === "prominent") {
    return (
      <>
        <button
          onClick={handleEmergencyCall}
          disabled={loading}
          className={`w-full flex items-center justify-center p-6 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 ${className}`}
        >
          {loading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3" />
          ) : (
            <Phone className="h-6 w-6 mr-3" />
          )}
          <div className="text-left">
            <h4 className="font-bold text-white text-lg">🚨 Emergency Breakdown Hotline</h4>
            <p className="text-sm text-red-100 font-medium">24/7 Support: {formatHotlineForDisplay(hotlineNumber)}</p>
          </div>
        </button>

        {/* Desktop Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                  Emergency Hotline
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="text-center mb-6">
                <p className="text-gray-600 mb-2">JOP Emergency Breakdown Hotline:</p>
                {jobId && (
                  <p className="text-sm text-gray-500 mb-4">
                    Related to Job: {jobId.slice(0, 8).toUpperCase()}
                  </p>
                )}
                <div className="text-3xl font-bold text-red-600 mb-6">
                  {formatHotlineForDisplay(hotlineNumber)}
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={copyToClipboard}
                    className="jop-button-secondary flex-1 flex items-center justify-center"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy Number
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => window.location.href = `tel:${hotlineNumber.replace(/\s/g, '')}`}
                    className="jop-button flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700"
                  >
                    <Phone className="h-4 w-4 mr-2" />
                    Call Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <button
        onClick={handleEmergencyCall}
        disabled={loading}
        className={`flex items-center p-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors ${className}`}
      >
        {loading ? (
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3" />
        ) : (
          <Phone className="h-8 w-8 mr-3" />
        )}
        <div className="text-left">
          <h4 className="font-medium text-white">Emergency Breakdown</h4>
          <p className="text-sm text-red-100">24/7 Hotline: {formatHotlineForDisplay(hotlineNumber)}</p>
        </div>
      </button>

      {/* Desktop Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-2" />
                Emergency Hotline
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="text-center mb-6">
              <p className="text-gray-600 mb-2">JOP Emergency Breakdown Hotline:</p>
              {jobId && (
                <p className="text-sm text-gray-500 mb-4">
                  Related to Job: {jobId.slice(0, 8).toUpperCase()}
                </p>
              )}
              <div className="text-3xl font-bold text-red-600 mb-6">
                {formatHotlineForDisplay(hotlineNumber)}
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={copyToClipboard}
                  className="jop-button-secondary flex-1 flex items-center justify-center"
                >
                  {copied ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Number
                    </>
                  )}
                </button>
                <button
                  onClick={() => window.location.href = `tel:${hotlineNumber.replace(/\s/g, '')}`}
                  className="jop-button flex-1 flex items-center justify-center bg-red-600 hover:bg-red-700"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmergencyCallButton;